import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";

import {
  BulkCategoriesDialog,
  CategoryChips,
  CategoryPicker,
  ManageCategoriesDialog,
  WatchCategoriesDialog,
} from "../components/Categories";
import { CompanyPicker, usePickedCompany } from "../components/CompanyPicker";
import { ConfirmDialog, RenameDialog } from "../components/Dialog";
import { ErrorState } from "../components/ErrorState";
import { ListSkeleton } from "../components/Loading";
import { Menu } from "../components/Menu";
import { SituationStamp } from "../components/Stamp";
import { useToast } from "../components/Toast";
import type { WatchItem } from "../lib/api";
import { formatDate, formatDateTime, plural } from "../lib/format";
import {
  useAddWatch,
  useBulkWatches,
  useCategories,
  useExports,
  useImports,
  useMe,
  useRemoveWatch,
  useStartExport,
  useUpdateWatch,
  useWatches,
} from "../lib/queries";
import { formatSiren } from "../lib/siren";
import { useTitle } from "../lib/useTitle";

type Filter = "all" | "risk" | "clear";
const NO_CATEGORY = "none";

const FILTERS: { key: Filter; label: string; match: (w: WatchItem) => boolean }[] = [
  { key: "all", label: "Toutes", match: () => true },
  { key: "risk", label: "Procédure ou cessation", match: (w) => ["critical", "high"].includes(w.situation.level) },
  { key: "clear", label: "Sans procédure", match: (w) => ["ok", "unknown"].includes(w.situation.level) },
];

function normalize(text: string) {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export function Dashboard() {
  useTitle("Mes entreprises");
  const watches = useWatches();
  const categories = useCategories();
  const me = useMe().data;
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(params.get("ajouter") === "1");
  const [managing, setManaging] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const category = params.get("categorie") ?? "";
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const setCategory = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("categorie", value);
    else next.delete("categorie");
    next.delete("ajouter");
    setParams(next, { replace: true });
  };

  const items = useMemo(() => watches.data ?? [], [watches.data]);
  const inCategory = useMemo(() => {
    if (!category) return items;
    if (category === NO_CATEGORY) return items.filter((w) => w.categories.length === 0);
    return items.filter((w) => w.categories.some((c) => String(c.id) === category));
  }, [items, category]);

  const visible = useMemo(() => {
    const q = normalize(query.trim());
    const digits = query.replace(/\s/g, "");
    const matcher = FILTERS.find((f) => f.key === filter)!.match;
    return inCategory.filter((w) => {
      if (!matcher(w)) return false;
      if (!q) return true;
      if (/^\d+$/.test(digits) && w.siren.includes(digits)) return true;
      const text = `${w.label ?? ""} ${w.name ?? ""} ${w.city ?? ""} ${w.categories.map((c) => c.name).join(" ")}`;
      return normalize(text).includes(q);
    });
  }, [inCategory, filter, query]);

  // Selection only ever covers rows that are still listed.
  const selectedVisible = visible.filter((w) => selected.has(w.id));
  const allSelected = visible.length > 0 && selectedVisible.length === visible.length;

  if (watches.isError) return <ErrorState error={watches.error} onRetry={() => watches.refetch()} />;

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Mes entreprises</h1>
          <p>
            {watches.isSuccess
              ? items.length
                ? `${plural(items.length, "entreprise suivie", "entreprises suivies")}. Vérification chaque matin.`
                : "Aucune entreprise suivie pour l'instant."
              : "Chargement de vos entreprises"}
          </p>
          {me && !me.all_alerts && (
            <p>
              {me.alert_kinds.length
                ? `E-mails envoyés pour ${plural(me.alert_kinds.length, "type d'événement", "types d'événements")} seulement.`
                : "Aucun e-mail d'alerte : tous les types d'événements sont désactivés."}{" "}
              <Link to="/compte#alertes">Modifier</Link>
            </p>
          )}
        </div>
        <div className="actions">
          <Link className="button button--quiet" to="/importer">
            Importer un fichier
          </Link>
          {items.length > 0 && (
            <button type="button" className="button button--quiet" onClick={() => setManaging(true)}>
              Gérer les catégories
            </button>
          )}
          {items.length > 0 && <ExportButton watches={visible} filtered={visible.length < items.length} />}
          {!adding && items.length > 0 && (
            <button type="button" className="button" onClick={() => setAdding(true)}>
              Ajouter une entreprise
            </button>
          )}
        </div>
      </div>

      <ImportBanner />
      <ExportBanner />

      {(adding || (watches.isSuccess && items.length === 0)) && (
        <AddPanel onClose={items.length ? () => setAdding(false) : undefined} />
      )}

      {watches.isPending && <ListSkeleton />}

      {items.length > 0 && (
        <>
          <div className="toolbar">
            <div className="segmented" role="group" aria-label="Filtrer par situation">
              {FILTERS.map((f) => (
                <button key={f.key} type="button" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label} <span className="count">{inCategory.filter(f.match).length}</span>
                </button>
              ))}
            </div>
            <div className="toolbar__fields">
              <label className="visually-hidden" htmlFor="category-filter">
                Filtrer par catégorie
              </label>
              <select
                id="category-filter"
                className="input select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Toutes les catégories</option>
                {categories.data?.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name} ({c.watch_count})
                  </option>
                ))}
                <option value={NO_CATEGORY}>Sans catégorie</option>
              </select>
              <label className="visually-hidden" htmlFor="watch-filter">
                Filtrer par nom, ville, catégorie ou SIREN
              </label>
              <input
                id="watch-filter"
                className="input"
                type="search"
                placeholder="Filtrer par nom, ville ou SIREN"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {visible.length ? (
            <>
              <div className="list-head">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedVisible.length > 0 && !allSelected;
                    }}
                    onChange={() =>
                      setSelected(allSelected ? new Set() : new Set(visible.map((w) => w.id)))
                    }
                  />
                  {selectedVisible.length
                    ? `${plural(selectedVisible.length, "sélectionnée", "sélectionnées")} sur ${visible.length}`
                    : `Tout sélectionner (${visible.length})`}
                </label>
              </div>
              <ol className="watch-list">
                {visible.map((w) => (
                  <WatchRow key={w.id} watch={w} selected={selected.has(w.id)} onToggle={() => toggle(w.id)} />
                ))}
              </ol>
            </>
          ) : (
            <div className="empty">
              <h2>Aucune entreprise ne correspond</h2>
              <p>Modifiez les filtres ou la recherche pour retrouver vos entreprises.</p>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  setFilter("all");
                  setQuery("");
                  setCategory("");
                }}
              >
                Afficher toutes les entreprises
              </button>
            </div>
          )}
        </>
      )}

      {selectedVisible.length > 0 && (
        <BulkBar watches={selectedVisible} onDone={() => setSelected(new Set())} />
      )}
      <ManageCategoriesDialog open={managing} onClose={() => setManaging(false)} />
    </div>
  );
}

function ImportBanner() {
  const imports = useImports();
  const running = imports.data?.find((j) => j.status !== "done");
  if (!running) return null;
  const percent = running.total ? Math.round((running.processed / running.total) * 100) : 0;
  return (
    <div className="notice import-banner" role="status">
      <span>
        Import de <strong>{running.filename}</strong> en cours : {running.processed} sur {running.total} ({percent} %).
      </span>
      <Link to={`/importer?suivi=${running.id}`}>Voir l'avancement</Link>
    </div>
  );
}

function ExportButton({ watches, filtered }: { watches: WatchItem[]; filtered: boolean }) {
  const start = useStartExport();
  const toast = useToast();
  const exports = useExports();
  const running = exports.data?.some((j) => j.status === "pending" || j.status === "running");
  return (
    <button
      type="button"
      className="button button--quiet"
      disabled={!watches.length || running || start.isPending}
      title="Zip : tableau Excel des entreprises affichées, avis de situation INSEE et extraits RNE"
      onClick={() =>
        start.mutate(
          watches.map((w) => w.id),
          { onError: (error) => toast(error.message, "error") },
        )
      }
    >
      {filtered ? `Exporter la liste (${watches.length})` : "Exporter"}
    </button>
  );
}

function ExportBanner() {
  const exports = useExports();
  const [hidden, setHidden] = useState<number | null>(null);
  const latest = exports.data?.[0];
  if (!latest || latest.id === hidden) return null;

  if (latest.status === "pending" || latest.status === "running") {
    const percent = latest.total ? Math.round((latest.processed / latest.total) * 100) : 0;
    return (
      <div className="notice import-banner" role="status">
        <span>
          Export de {plural(latest.total, "entreprise", "entreprises")} en cours : {latest.processed} sur{" "}
          {latest.total} ({percent} %). Les documents sont téléchargés auprès de l'INSEE et de l'INPI.
        </span>
      </div>
    );
  }
  const hide = (
    <button type="button" className="button button--link" onClick={() => setHidden(latest.id)}>
      Masquer
    </button>
  );
  if (latest.status === "failed") {
    return (
      <div className="notice notice--error import-banner" role="status">
        <span>{latest.error ?? "L'export a échoué : relancez-le."}</span>
        {hide}
      </div>
    );
  }
  if (!latest.download_url) return null;
  return (
    <div className="notice notice--success import-banner" role="status">
      <span>
        Export prêt : {plural(latest.total, "entreprise", "entreprises")},{" "}
        {plural(latest.documents, "document", "documents")}
        {latest.missing > 0 &&
          ` (${latest.missing} non ${latest.missing > 1 ? "délivrés" : "délivré"}, détail dans le tableau)`}
        . Disponible jusqu'au{" "}
        {formatDateTime(latest.expires_at)}.
      </span>
      <span className="actions">
        <a className="button button--small" href={latest.download_url} download={latest.filename}>
          Télécharger le zip
        </a>
        {hide}
      </span>
    </div>
  );
}

function BulkBar({ watches, onDone }: { watches: WatchItem[]; onDone: () => void }) {
  const bulk = useBulkWatches();
  const toast = useToast();
  const [mode, setMode] = useState<"add" | "remove" | null>(null);
  const [stopping, setStopping] = useState(false);
  const ids = watches.map((w) => w.id);
  const count = plural(ids.length, "entreprise", "entreprises");

  const run = (change: { add_category_ids?: number[]; remove_category_ids?: number[]; stop?: boolean }, message: string) =>
    bulk.mutate(
      { watch_ids: ids, ...change },
      {
        onSuccess: () => {
          toast(message);
          setMode(null);
          setStopping(false);
          if (change.stop) onDone();
        },
        onError: (error) => toast(error.message, "error"),
      },
    );

  return (
    <div className="bulk-bar" role="region" aria-label="Actions sur la sélection">
      <div className="page bulk-bar__inner">
        <strong>{count} sélectionnée{ids.length > 1 ? "s" : ""}</strong>
        <div className="actions">
          <button type="button" className="button button--small" onClick={() => setMode("add")}>
            Ajouter à des catégories
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setMode("remove")}>
            Retirer de catégories
          </button>
          <button type="button" className="button button--quiet button--small" onClick={() => setStopping(true)}>
            Arrêter le suivi
          </button>
          <button type="button" className="button button--link" onClick={onDone}>
            Désélectionner
          </button>
        </div>
      </div>
      <BulkCategoriesDialog
        mode={mode}
        count={ids.length}
        pending={bulk.isPending}
        onClose={() => setMode(null)}
        onApply={(categoryIds) =>
          mode === "remove"
            ? run({ remove_category_ids: categoryIds }, `Catégories retirées de ${count}.`)
            : run({ add_category_ids: categoryIds }, `${count} rangée${ids.length > 1 ? "s" : ""} dans les catégories.`)
        }
      />
      <ConfirmDialog
        open={stopping}
        onClose={() => setStopping(false)}
        title={`Arrêter le suivi de ${count} ?`}
        body="Vous ne recevrez plus d'alerte pour ces entreprises. Vous pourrez les rajouter plus tard."
        confirmLabel="Arrêter le suivi"
        danger
        pending={bulk.isPending}
        onConfirm={() => run({ stop: true }, `Suivi arrêté pour ${count}.`)}
      />
    </div>
  );
}

function AddPanel({ onClose }: { onClose?: () => void }) {
  const [siren, setSiren] = useState("");
  const [label, setLabel] = useState("");
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const picked = usePickedCompany(siren);
  const add = useAddWatch();
  const toast = useToast();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!picked.ready) return;
    add.mutate(
      { siren, label: label.trim() || undefined, category_ids: categoryIds },
      {
        onSuccess: (result) => {
          toast(`${result.name ?? "L'entreprise"} est maintenant suivie.`);
          setSiren("");
          setLabel("");
          add.reset();
        },
      },
    );
  }

  return (
    <form className="sheet add-panel" onSubmit={submit} aria-labelledby="add-title">
      <div className="add-panel__head">
        <h2 id="add-title" className="watch-form__title">
          {onClose ? "Ajouter une entreprise" : "Ajoutez votre première entreprise"}
        </h2>
        {onClose ? (
          <button type="button" className="button button--quiet button--small" onClick={onClose}>
            Fermer
          </button>
        ) : (
          <Link to="/importer">Vous en avez beaucoup ? Importez un fichier</Link>
        )}
      </div>
      <CompanyPicker siren={siren} onChange={setSiren} size="compact" autoFocus />
      <div className="field">
        <label className="field__label" htmlFor="add-label">
          Votre nom pour cette entreprise <span className="field__hint">(facultatif)</span>
        </label>
        <input
          id="add-label"
          className="input"
          maxLength={200}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex. : Client principal Lyon"
        />
      </div>
      <CategoryPicker selected={categoryIds} onChange={setCategoryIds} legend="Catégories (facultatif)" />
      {add.isError && (
        <div className="notice notice--error" role="alert">
          {add.error.message}
        </div>
      )}
      <div>
        <button type="submit" className="button" disabled={!picked.ready || add.isPending}>
          {add.isPending ? "Ajout en cours" : "Suivre cette entreprise"}
        </button>
      </div>
    </form>
  );
}

function WatchRow({ watch, selected, onToggle }: { watch: WatchItem; selected: boolean; onToggle: () => void }) {
  const [renaming, setRenaming] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const update = useUpdateWatch(watch.siren);
  const remove = useRemoveWatch();
  const toast = useToast();
  const title = watch.label ?? watch.name ?? formatSiren(watch.siren);
  const event = watch.latest_event;

  return (
    <li className={`watch-row watch-row--${watch.situation.level}${selected ? " is-selected" : ""}`}>
      <label className="watch-row__select">
        <input type="checkbox" checked={selected} onChange={onToggle} />
        <span className="visually-hidden">Sélectionner {title}</span>
      </label>
      <div className="watch-row__situation">
        <SituationStamp situation={watch.situation} />
        {watch.situation.since && (
          <span className="watch-row__since">depuis le {formatDate(watch.situation.since, "short")}</span>
        )}
      </div>
      <div className="watch-row__identity">
        <Link className="watch-row__name" to={`/entreprise/${watch.siren}`}>
          {title}
        </Link>
        <div className="watch-row__meta">
          {watch.label && watch.name ? `${watch.name}, ` : ""}
          <span className="nowrap">{formatSiren(watch.siren)}</span>
          {watch.city && `, ${watch.city}`}
        </div>
        <CategoryChips categories={watch.categories} />
      </div>
      <div className="watch-row__event">
        {event ? (
          <>
            Dernière annonce le {formatDate(event.published_on, "short")}
            <strong>{event.title}</strong>
          </>
        ) : watch.last_checked_at ? (
          "Aucune annonce publiée"
        ) : (
          "Première vérification en cours"
        )}
      </div>
      <div className={`watch-row__alerts${watch.recent_alerts ? " watch-row__alerts--new" : ""}`}>
        {watch.recent_alerts
          ? plural(watch.recent_alerts, "alerte ce mois-ci", "alertes ce mois-ci")
          : "Rien de neuf ce mois-ci"}
      </div>
      <div className="watch-row__menu">
        <Menu
          label={`Actions pour ${title}`}
          actions={[
            { label: "Modifier les catégories", onSelect: () => setCategorizing(true) },
            { label: "Renommer pour moi", onSelect: () => setRenaming(true) },
            { label: "Arrêter le suivi", onSelect: () => setRemoving(true), danger: true },
          ]}
        />
      </div>
      <WatchCategoriesDialog watch={watch} open={categorizing} onClose={() => setCategorizing(false)} />
      <RenameDialog
        open={renaming}
        onClose={() => setRenaming(false)}
        initial={watch.label ?? ""}
        companyName={watch.name ?? watch.siren}
        pending={update.isPending}
        onSave={(label) =>
          update.mutate(
            { id: watch.id, label },
            {
              onSuccess: () => {
                setRenaming(false);
                toast("Nom enregistré.");
              },
              onError: (error) => toast(error.message, "error"),
            },
          )
        }
      />
      <ConfirmDialog
        open={removing}
        onClose={() => setRemoving(false)}
        title={`Arrêter le suivi de ${title} ?`}
        body="Vous ne recevrez plus d'alerte pour cette entreprise. Vous pourrez la rajouter plus tard."
        confirmLabel="Arrêter le suivi"
        danger
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { id: watch.id, siren: watch.siren },
            {
              onSuccess: () => {
                setRemoving(false);
                toast(`Suivi de ${title} arrêté.`);
              },
              onError: (error) => toast(error.message, "error"),
            },
          )
        }
      />
    </li>
  );
}
